import { faEnvelope, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Link } from 'react-router-dom';
import Logo from '../logo';
import {
  POLICY_PAGES,
  PolicyDocument,
  brandPolicies,
  brandPolicyTitle,
  getBrand,
} from '../../constants/brands';

/** Linked by hand above (account deletion is reached from the policy pages). */
const FOOTER_FIXED_POLICIES: PolicyDocument[] = [
  'privacy',
  'terms',
  'cancellation',
  'account-deletion',
];

const FooterContainer = styled.footer`
  ${tw`
    flex
    flex-col
    w-full
    max-w-full
    min-w-0
    items-center
    overflow-x-hidden
    box-border
  `};
  background-color: var(--color-bg-secondary);
  border-top: 1px solid var(--color-border);
`;

const InnerContainer = styled.div`
  ${tw`
    grid
    w-full
    max-w-screen-xl
    gap-10
    px-4 md:px-6 lg:px-8
    pt-12 md:pt-16
    pb-10
    grid-cols-1
    sm:grid-cols-2
    lg:grid-cols-[1.4fr_1fr_1fr_1fr]
  `};
`;

const BrandColumn = styled.div`
  ${tw`flex flex-col gap-4 min-w-0`}
`;

const BrandTagline = styled.p`
  ${tw`text-sm max-w-xs leading-6`}
  color: var(--color-text-secondary);
`;

const BottomContainer = styled.div`
  ${tw`
    w-full
    max-w-screen-xl
    flex
    flex-col
    sm:flex-row
    sm:items-center
    sm:justify-between
    gap-2
    px-4 md:px-6 lg:px-8
    py-5
  `};
  border-top: 1px solid var(--color-border);
`;

const CopyrightText = styled.small`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;

const SectionContainer = styled.div`
  ${tw`flex flex-col min-w-0`}
`;

const LinksList = styled.ul`
  ${tw`outline-none list-none flex flex-col gap-2.5 m-0 p-0`};
`;

const ListItem = styled.li`
  & > a {
    ${tw`text-sm no-underline`};
    color: var(--color-text-secondary);
    transition: color var(--transition-normal);
    &:hover {
      color: var(--color-text-primary);
    }
  }
`;

const HeaderTitle = styled.h3`
  ${tw`text-xs font-semibold uppercase tracking-wider mb-4`};
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  letter-spacing: 0.08em;
`;

const ContactRow = styled.a`
  ${tw`flex items-center gap-3 text-sm no-underline`}
  color: var(--color-text-secondary);
  & + & {
    margin-top: 12px;
  }
  &:hover {
    color: var(--color-text-primary);
  }
`;

const ContactIcon = styled.span`
  ${tw`flex items-center justify-center w-9 h-9 rounded-lg shrink-0`}
  background-color: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  color: var(--color-black);
`;

export function Footer() {
  const brand = getBrand();
  const year = new Date().getFullYear();
  return (
    <FooterContainer>
      <InnerContainer>
        <BrandColumn>
          <Logo color="dark" />
          <BrandTagline>{brand.copy.heroSubcopy}</BrandTagline>
        </BrandColumn>
        <SectionContainer>
          <HeaderTitle>Company</HeaderTitle>
          <LinksList>
            <ListItem>
              <Link to="/">Home</Link>
            </ListItem>
            <ListItem>
              <Link to="/about-us">About Us</Link>
            </ListItem>
            <ListItem>
              <Link to="/help">Help Center</Link>
            </ListItem>
          </LinksList>
        </SectionContainer>
        <SectionContainer>
          <HeaderTitle>Legal</HeaderTitle>
          <LinksList>
            <ListItem>
              <Link to="/policies/privacy">Privacy Policy</Link>
            </ListItem>
            <ListItem>
              <Link to="/policies/terms">Terms &amp; Conditions</Link>
            </ListItem>
            <ListItem>
              <Link to="/policies/cancellation">Cancellation Policy</Link>
            </ListItem>
            {brandPolicies(brand)
              .filter(doc => !FOOTER_FIXED_POLICIES.includes(doc))
              .map(doc => (
                <ListItem key={doc}>
                  <Link to={POLICY_PAGES[doc].route}>{brandPolicyTitle(doc, brand)}</Link>
                </ListItem>
              ))}
            <ListItem>
              <Link to="/phone-verification-opt-in">SMS Opt-In Process</Link>
            </ListItem>
          </LinksList>
        </SectionContainer>
        <SectionContainer>
          <HeaderTitle>Contact</HeaderTitle>
          {brand.supportPhone && (
            <ContactRow href={`tel:${brand.supportPhone.replace(/[^\d+]/g, '')}`}>
              <ContactIcon aria-hidden>
                <FontAwesomeIcon icon={faPhoneAlt} />
              </ContactIcon>
              <span>{brand.supportPhone}</span>
            </ContactRow>
          )}
          <ContactRow href={`mailto:${brand.supportEmail}`}>
            <ContactIcon aria-hidden>
              <FontAwesomeIcon icon={faEnvelope} />
            </ContactIcon>
            <span>{brand.supportEmail}</span>
          </ContactRow>
        </SectionContainer>
      </InnerContainer>
      <BottomContainer>
        <CopyrightText>
          &copy; {year} {brand.legalName}. All rights reserved.
        </CopyrightText>
        <CopyrightText>{brand.brandSite}</CopyrightText>
      </BottomContainer>
    </FooterContainer>
  );
}
